/** Live voice practice — speak, and be answered out loud.
 *
 * The client asked for this knowing the price: "they know its costly but
 * the user is paying for it". That shapes the whole screen. This is the
 * one AI surface where leaving a tab open spends real money, so the price
 * per minute, the balance and the time remaining are all on screen BEFORE
 * anything opens and while it runs — not buried in a receipt afterwards.
 *
 * It is paid from the purchased AI balance and never from the free monthly
 * allowance, and the screen says so plainly. A learner who finds out
 * afterwards that one conversation spent their included AI has been
 * treated badly, however correct the arithmetic was.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../../api/liveVoice";
import type { LiveVoiceStatus } from "../../api/liveVoice";
import { ApiError } from "../../api/http";
import { Button, Card, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { rupeeCost } from "../../utils/rupees";
import { useLiveVoice } from "./useLiveVoice";

const mmss = (total: number) =>
  `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;

export function LiveVoicePage() {
  const t = useT();
  const [status, setStatus] = useState<LiveVoiceStatus | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [voice, setVoice] = useState<string | null>(null);
  const live = useLiveVoice();

  const load = () =>
    api
      .liveVoiceStatus()
      .then((s) => {
        setStatus(s);
        setVoice((v) => v ?? s.voices[0]?.id ?? null);
      })
      .catch((e) =>
        setBlocked(e instanceof ApiError ? e.message : t("live.unavailable")),
      );
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a conversation the balance has moved, so re-read it rather than
  // leaving a stale number on a screen that is all about money.
  useEffect(() => {
    if (live.result) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.result]);

  if (blocked) {
    return (
      <Card>
        <p className="text-sm text-muted">{blocked}</p>
      </Card>
    );
  }
  if (!status) {
    return (
      <div className="grid place-items-center py-10">
        <Spinner />
      </div>
    );
  }
  if (!status.configured) {
    return (
      <Card>
        <p className="text-sm text-muted">{t("live.notConfigured")}</p>
      </Card>
    );
  }

  const chosen = status.voices.find((v) => v.id === voice) ?? status.voices[0];
  const affordable = chosen ? (status.affordable_seconds[chosen.id] ?? 0) : 0;
  const tooPoor = affordable < status.min_session_seconds;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold text-ink">{t("live.title")}</h1>
        <p className="mt-1 text-muted">{t("live.intro")}</p>
      </div>

      {/* The money, before anything opens. */}
      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <span className="text-sm font-semibold text-ink">
            {t("live.balance")}
          </span>
          <span className="text-2xl font-extrabold text-ink tabular-nums">
            {rupeeCost(status.balance_minor)}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted">{t("live.fromBalance")}</p>
      </Card>

      {live.state === "idle" && (
        <>
          <section>
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted">
              {t("live.choose")}
            </h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {status.voices.map((v) => {
                const on = chosen?.id === v.id;
                const mins = Math.floor(
                  (status.affordable_seconds[v.id] ?? 0) / 60,
                );
                return (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setVoice(v.id)}
                    className={`rounded-control border p-3 text-left ${
                      on ? "border-brand bg-brand-soft" : "border-line hover:border-brand"
                    }`}
                  >
                    <span className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-bold text-ink">{v.label}</span>
                      <span className="text-sm font-extrabold text-brand-dark tabular-nums">
                        {t("live.perMinute", {
                          price: rupeeCost(v.price_per_minute_minor),
                        })}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs text-muted">{v.blurb}</span>
                    {/* What their money actually buys on this voice, which
                        is the number they are really choosing between. */}
                    <span className="mt-1 block text-xs font-semibold text-muted">
                      {t("live.affords", { minutes: mins })}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {tooPoor ? (
            <Card>
              <p className="text-sm text-ink">{t("live.topUpNeeded")}</p>
              <Link
                to="/store"
                className="mt-3 inline-block rounded-control bg-brand px-4 py-2 text-sm font-semibold text-white"
              >
                {t("credits.topUp")}
              </Link>
            </Card>
          ) : (
            <Button onClick={() => void live.start({ model: chosen?.id })}>
              {t("live.start")}
            </Button>
          )}
        </>
      )}

      {live.state === "connecting" && (
        <div className="flex items-center gap-3">
          <Spinner />
          <span className="text-sm text-muted">{t("live.connecting")}</span>
        </div>
      )}

      {live.state === "live" && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm font-bold text-ink">
              <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-danger" />
              {t("live.listening")}
            </span>
            {/* The clock is the bill. It is the biggest thing on the card
                while a conversation is running, on purpose. */}
            <span className="text-2xl font-extrabold tabular-nums text-ink">
              {mmss(live.secondsLeft)}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted">{t("live.timeLeft")}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void live.stop()}>
            {t("live.stop")}
          </Button>
        </Card>
      )}

      {live.result && live.state !== "live" && (
        <Card>
          <p className="text-sm text-ink">
            {t("live.spent", {
              time: mmss(live.result.seconds),
              amount: rupeeCost(live.result.spent_minor),
            })}
          </p>
        </Card>
      )}

      {live.error && (
        <Card>
          <p className="text-sm text-danger">{live.error}</p>
        </Card>
      )}

      {/* Where the partner's voice comes out. */}
      <audio ref={live.audioRef} autoPlay data-testid="live-audio" />
    </div>
  );
}
